import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { BlurView } from 'expo-blur';
import GradientButton from './GradientButton';
import { useNavigation } from '../context/NavigationContext';

const QueryLimitModal = ({ visible, onClose, planType, queriesUsed, queryLimit }) => {
  const { navigateTo } = useNavigation();

  if (!visible) return null;

  const getPlanMessage = () => {
    switch (planType) {
      case 'FREE':
        return {
          title: 'Free Queries Exhausted',
          message: `You've used all ${queryLimit} free queries this month. Upgrade to continue optimizing your bets.`,
          upgradeText: 'View Plans',
        };
      case 'BASIC':
        return {
          title: 'Query Limit Reached',
          message: `You've used all ${queryLimit} queries this month on the Basic plan. Upgrade to Pro for more queries.`,
          upgradeText: 'Upgrade to Pro',
        };
      case 'PRO':
        return {
          title: 'Query Limit Reached',
          message: `You've used all ${queryLimit} queries this month on the Pro plan. Upgrade to Ultimate for unlimited queries.`,
          upgradeText: 'Upgrade to Ultimate',
        };
      default:
        return {
          title: 'Query Limit Reached',
          message: 'You have reached your monthly query limit.',
          upgradeText: 'View Plans',
        };
    }
  };

  const { title, message, upgradeText } = getPlanMessage();

  const handleUpgrade = () => {
    onClose();
    navigateTo('Plans');
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity 
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <BlurView intensity={80} tint="dark" style={styles.blurContainer}>
          <TouchableOpacity 
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            style={styles.modalContainer}
          >
            <View style={styles.content}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.message}>{message}</Text>
              
              <View style={styles.stats}>
                <Text style={styles.statsText}>
                  Queries used: <Text style={styles.statsNumber}>{queriesUsed}</Text> / {queryLimit === 'unlimited' ? '∞' : queryLimit}
                </Text>
              </View>

              <View style={styles.buttonContainer}>
                <GradientButton
                  text={upgradeText}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    handleUpgrade();
                  }}
                  style={styles.upgradeButton}
                  textStyle={styles.upgradeButtonText}
                  gradientColors={["#707070", "#EFEFEF"]}
                  fontSize={16}
                  fontFamily="ClimateCrisis"
                  fontWeight="400"
                />
                
                <TouchableOpacity 
                  onPress={(e) => {
                    e.stopPropagation();
                    onClose();
                  }} 
                  style={styles.closeButton}
                >
                  <Text style={styles.closeButtonText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </BlurView>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  blurContainer: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 500,
    backgroundColor: 'rgba(19, 19, 19, 0.95)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.5,
        shadowRadius: 20,
      },
    }),
  },
  content: {
    padding: 32,
    gap: 20,
  },
  title: {
    fontSize: 28,
    fontFamily: 'ClimateCrisis',
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.95)',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  message: {
    fontSize: 16,
    fontFamily: 'Slate',
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.75)',
    textAlign: 'center',
    lineHeight: 24,
  },
  stats: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
  },
  statsText: {
    fontSize: 15,
    fontFamily: 'Slate',
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
  },
  statsNumber: {
    fontSize: 18,
    fontFamily: 'Slate',
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.95)',
  },
  buttonContainer: {
    gap: 12,
    marginTop: 8,
  },
  upgradeButton: {
    width: '100%',
    paddingVertical: 16,
  },
  upgradeButtonText: {
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  closeButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 15,
    fontFamily: 'Slate',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.5)',
  },
});

export default QueryLimitModal;


